import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  model,
  output,
  ViewEncapsulation,
  inject,
  DestroyRef,
  viewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../button/button.component';
import { FaIconComponent } from '../../atoms';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { faCircleXmark, faFileVideo } from '@fortawesome/free-solid-svg-icons';
import { KindPipe } from '../../../pipes';
import { MessageKind } from '@clientwire/api';
import { TextFieldModule } from '@angular/cdk/text-field';
import { FilesToUpload } from '../../../interfaces';
import { AutosizeModule } from 'ngx-autosize';
import { Subscription, tap, throttleTime } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export type InputAddon = {
  button?: boolean;
  outlined?: boolean;
  flat?: boolean;
  icon?: IconProp;
  label?: string;
  ariaLabel?: string;
  vAlign?: 'center' | 'flex-end' | 'flex-start';
  disabled?: boolean;
  testId?: string;
};

interface Logger {
  error(message: string, ...args: unknown[]): void;
}

@Component({
  selector: 'orbit-input',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    ReactiveFormsModule,
    FaIconComponent,
    KindPipe,
    TextFieldModule,
    AutosizeModule,
  ],
  templateUrl: './input.component.html',
  styleUrl: './input.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputComponent {
  readonly #defaultController = new FormControl<string | null>(null);
  //#region Inputs
  addonLeft = input<InputAddon>();
  addonRight = input<InputAddon>();
  autocomplete = input<boolean>(true);
  controller = input<FormControl>(this.#defaultController);
  disabled = input<boolean, unknown>(false, { transform: booleanAttribute });
  inputId = input<string>();
  ariaLabel = input<string>();
  inputSourceId = input<string>();
  inputType = input<'text' | 'number' | 'email' | 'password' | 'textarea'>('text');
  ariaDescribedBy = input<string>();
  large = input<boolean>(false);
  loading = input<boolean>(false);
  logger = input<Logger | null>(null);
  name = input<string>('');
  placeholder = input<string>();
  preventTyping = input<boolean>(false);
  rounded = input<boolean, unknown>(false, { transform: booleanAttribute });
  textareaAutosizeLimit = input<{ minRows: number; maxRows: number }>({ minRows: 1, maxRows: 10 });
  testId = input<string>();
  //#endregion

  //#region Models
  files = model<FilesToUpload[]>();
  inputValue = model<string>();
  //#endregion

  //#region Outputs
  addonButtonClicked = output<boolean>();
  addonIconClicked = output<boolean>();
  deleteAttachmentEvent = output<MessageKind>();
  enterPressed = output<boolean>();
  isTyping = output<boolean>();
  blurred = output<void>();
  //#endregion

  //#region Variables
  closeIcon = faCircleXmark;
  videoFileIcon = faFileVideo;
  readonly #allowedFileTypes: MessageKind[] = ['IMAGE', 'VIDEO', 'AUDIO'];
  #controllerSubscription: Subscription | null = null;
  readonly #destroyRef = inject(DestroyRef);
  textareaRef = viewChild('textarea', { read: ElementRef });
  inputRef = viewChild('input', { read: ElementRef });
  //#endregion

  //#region Computed
  hasAddon = computed(() => this.#calculateHasAddon());
  //#endregion

  constructor() {
    effect(() => {
      if (this.controller()) this.subscribeToControllerValueChanges();
    });
    effect(() => {
      if (this.disabled()) {
        this.controller().disable();
      } else {
        this.controller().enable();
      }
    });
    effect(() => {
      const inputValue = this.inputValue();
      if (inputValue) {
        this.#defaultController.setValue(inputValue);
      }
    });
    effect(() => {
      const files = this.files();
      if (files) {
        this.#processFilesForPreview(files);
      }
    });
    effect(() => {
      if (this.inputSourceId()) this.subscribeToControllerValueChanges();
    });
  }

  typing(event: Event) {
    if (this.preventTyping()) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  subscribeToControllerValueChanges() {
    if (this.#controllerSubscription) {
      this.#controllerSubscription.unsubscribe();
    }
    this.#controllerSubscription = this.controller()
      .valueChanges.pipe(
        throttleTime(5000),
        tap((data) => {
          return data;
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: (data) => {
          if (data?.length > 0) {
            this.isTyping.emit(true);
          }
        },
        error: (err) => {
          if (this.logger()) {
            try {
              const logger = this.logger();
              if (logger) {
                logger.error('Error: %s', err);
              }
            } catch {
              console.error('Error: %s', err);
            }
          }
        },
      });
  }

  emitEnterPressed($event: Event, preventDefault = false) {
    if (preventDefault) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (!this.loading()) {
      this.enterPressed.emit(true);
    }
  }

  emitAddonButtonClicked() {
    this.addonButtonClicked.emit(true);
  }

  onAddonButtonKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!this.loading() && !this.disabled()) {
        this.emitAddonButtonClicked();
      }
    }
  }

  emitAddonIconClicked() {
    this.addonIconClicked.emit(true);
  }

  onAddonIconClick(addon: InputAddon | undefined) {
    if (this.loading() || this.disabled() || addon?.disabled) {
      return;
    }
    this.emitAddonIconClicked();
  }

  focusInput(): void {
    if (this.inputType() === 'textarea') {
      this.textareaRef()?.nativeElement?.focus();
    } else {
      this.inputRef()?.nativeElement?.focus();
    }
  }

  deleteAttachment(index: number) {
    const file = this.files()?.[index];
    // Clean the local file array
    this.files.update((files) => {
      if (!files) return files;
      const array = [...files];
      array.splice(index, 1);
      return array;
    });
    // Clean the form input
    if (file) {
      const fileKind = file.file?.type.split('/')[0]?.toUpperCase();
      if (this.#allowedFileTypes.includes(fileKind as MessageKind)) {
        this.deleteAttachmentEvent.emit(fileKind as MessageKind);
      }
    }
  }

  onDeleteAttachmentKeyDown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.deleteAttachment(index);
    }
  }

  onAddonIconKeyDown(event: KeyboardEvent, addon: InputAddon | undefined): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onAddonIconClick(addon);
    }
  }

  #calculateHasAddon() {
    return (
      this.addonLeft()?.button ||
      this.addonLeft()?.icon ||
      this.addonLeft()?.label ||
      this.addonRight()?.button ||
      this.addonRight()?.icon ||
      this.addonRight()?.label
    );
  }

  #processFilesForPreview(files: FilesToUpload[]) {
    files.forEach((file, index) => {
      if (!file.src) {
        this.#loadFilePreview(file, index);
      }
    });
  }

  #loadFilePreview(file: FilesToUpload, index: number) {
    const reader = new FileReader();
    reader.onload = () => this.#updateFileWithPreview(index, reader.result as string);
    reader.readAsDataURL(file.file);
  }

  #updateFileWithPreview(index: number, previewSrc: string) {
    this.files.update((files) => {
      if (files?.[index]) {
        files[index].src = previewSrc;
        return [...files];
      }
      return files;
    });
  }
}
